import {
	vultureENV,
} from 'Vulture/ENV';
import { s3PublicURL } from 'Vulture/S3.js';

const IMAGE_FORMATS = [
	'jpg',
	'jpeg',
	'png',
	'webp',
];

const VIDEO_FORMATS = [
	'mp4',
	'webm',
];

// Document formats are selectable only in PDF contexts (DEV-11642 part 2).
export const DOCUMENT_FORMATS = [
	'pdf',
];

// Max document (PDF) upload size, in MB. Mirrors the backend's enforced cap
// (library/v1/consts.go MaxDocumentUploadBytes = 50 MB) — keep in sync.
export const DOCUMENT_MAX_UPLOAD_MB = 50;

// Media formats only (images + videos). This is the default upload/validation
// set and the image-picker default — it must NOT include documents, so PDFs
// stay out of image components, slideshows, and slide decks (DEV-11642 part 3).
export const VALID_FORMATS = [
	...IMAGE_FORMATS,
	...VIDEO_FORMATS,
];

export const VALID_EXTENSIONS = VALID_FORMATS.map((ext) => {
	return `.${ext}`;
});

// Everything the Library itself accepts — media plus documents. Use this only
// for direct library uploads (CreateFileModal), never for slideshow/slide-deck
// or image-component contexts.
export const LIBRARY_FORMATS = [
	...VALID_FORMATS,
	...DOCUMENT_FORMATS,
];

export const LIBRARY_EXTENSIONS = LIBRARY_FORMATS.map((ext) => {
	return `.${ext}`;
});

const getFileExtension = (filename) => {
	return filename.toLowerCase().split('.').pop();
};

// Documents (PDFs) have no generated thumbnail; renderers use this to show a
// default PDF icon instead of requesting a thumbnail that 404s.
export const isDocumentFile = (filename) => {
	return DOCUMENT_FORMATS.includes(getFileExtension(filename || ''));
};

// A library file the preview (zoom) button can open: images go to the zoom
// modal, PDFs to the PDF viewer (DEV-12038). Everything else has no viewer.
export const isPreviewable = (file) => {
	if (!file || file.isDir) return false;
	return IMAGE_FORMATS.includes(getFileExtension(file.name || '')) || isDocumentFile(file.name);
};

const isValidFileFormat = (filename, formats) => {
	const extension = getFileExtension(filename);
	return formats.includes(extension);
};

// validateFiles splits files by whether their extension is allowed for the
// caller's context. Defaults to media-only (VALID_FORMATS); the Library passes
// LIBRARY_FORMATS to also accept PDFs.
export const validateFiles = (files, formats = VALID_FORMATS) => {
	const validFiles = [];
	const invalidFiles = [];

	files.forEach((file) => {
		if (isValidFileFormat(file.name, formats)) {
			validFiles.push(file);
		} else {
			invalidFiles.push(file);
		}
	});

	return {
		validFiles,
		invalidFiles,
	};
};

export const formatBytes = (bytes) => {
	if (bytes === 0 || bytes === undefined || bytes === null) return '-';
	const k = 1024;
	const sizes = [ 'B', 'KB', 'MB', 'GB', 'TB', ];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getDownloadConfirmMessage = (stats = {}) => {
	const {
		numFiles = 0,
		numFolders = 0,
		numSubFolders = 0,
		totalSize = 0,
	} = stats;

	const lines = [
		'Are you sure you want to download the selected items to your computer?',
		'',
	];

	if (numFiles > 0) {
		lines.push(`Files: ${numFiles}`);
	}

	if (numFolders > 0) {
		const subFolderLabel = numSubFolders === 1 ? 'sub-folder' : 'sub-folders';
		const contents = numSubFolders > 0 ?
			`(including ${subFolderLabel} & contents)` :
			'(including contents)';
		lines.push(`Folders: ${numFolders} ${contents}`);
	}

	if (totalSize > 0) {
		lines.push(`Total size: ${formatBytes(totalSize)}`);
	}

	return lines.join('\n');
};

const encodeThumbPath = (path) => {
	return encodeURIComponent(path)
		.replace(/'/g, '%27')
		.replace(/\(/g, '%28')
		.replace(/\)/g, '%29')
		.replace(/!/g, '%21')
		.replace(/\*/g, '%2A');
};

export const getThumbnailPath = (filePath) => {
	if (!filePath) return '';

	const webpPath = filePath.replace(/\.[^/.]+$/, '.webp');

	return s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library-thumbnail/${encodeThumbPath(webpPath)}`);
};

// Resolve the resized-thumbnail URL for a non-library slide file.
//
// Routing is by path SHAPE, never by whether the path contains the current
// account UUID. Moved or copied slides keep the *source* account's UUID (legacy
// pelican paths) or a hashed `uploads/<md5>/` segment, so an account-match check
// misroutes them to the wrong bucket and yields broken thumbnails. See DEV-11897.
//
//   uploads/<md5>/<name>.<ext>     -> ts-condor-assets/<env>/ts-library-thumbnail/uploads/<md5>/<name>.webp
//   <env>/<uuid>/...               -> ts-pelican-resized/<file> (stored key as-is; videos get a "--video.jpg" poster)
export const getSlideThumbnailURL = (filePath, kind) => {
	if (!filePath) return '';

	// Anything that is not an `uploads/` path is legacy pelican content in the
	// ts-pelican-resized bucket. Use the stored key as-is — it already carries its
	// own <env>/<uuid>/ prefix, and prepending the running env would corrupt any
	// key whose stored env differs from it.
	if (!filePath.startsWith('uploads/')) {
		let key = filePath;

		// Pelican video posters are stored next to the source file as "<file>--video.jpg".
		if (kind && kind.startsWith('video')) {
			key += '--video.jpg';
		}

		return s3PublicURL('ts-pelican-resized', `${encodeThumbPath(key)}`);
	}

	// uploads/<md5>/... resized output (images and video posters alike) is the
	// .webp sibling in the ts-library-thumbnail tree.
	const webpPath = filePath.replace(/\.[^/.]+$/, '.webp');

	return s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library-thumbnail/${encodeThumbPath(webpPath)}`);
};

export const getFilePath = (filePath) => {
	if (!filePath) return '';

	return s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${encodeThumbPath(filePath)}`);
};
